'use no memo';

/* eslint-disable complexity */
import { getTrafficSource, isSessionStorageAllowed } from '@local/utils';

import { getFromChownowApi, RESTAURANT_API_VERSION } from 'helpers/api';
import { getRestaurantEndpoint } from 'helpers/restaurantApi';

import {
  DEPLOY_ENVIRONMENTS,
  MARKETPLACE_ORIGINS,
  NO_DIRECT_LIVE_CHANNEL,
} from 'helpers/constants';
import {
  logAnalyticsEvent,
  logMessage,
  ANALYTICS_EVENT_NAME,
} from 'helpers/loggers';
import { isDirectUrl, getHostnameForMarketplace } from 'helpers/url';
import locationReplace from 'helpers/locationUtils';

/**
 * Returns a boolean that describes if a restaurant is live for Marketplace (formerly Discover)
 * @param locationID {string} restaurant location ID
 * @returns {boolean} representing live for discover status
 */
export async function getRestaurantIsLiveForMarketplace(locationID) {
  try {
    const { data } = await getFromChownowApi({
      endpoint: getRestaurantEndpoint(locationID),
      apiVersion: RESTAURANT_API_VERSION,
    });
    return !!data.is_discover_live;
  } catch {
    return false;
  }
}

export function shouldNavigateToMarketplace(restaurant) {
  const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
  if (deployEnv === DEPLOY_ENVIRONMENTS.local) return false;

  const cnChannel = getTrafficSource(restaurant.id);

  if (!isDirectUrl() || (cnChannel && cnChannel !== NO_DIRECT_LIVE_CHANNEL)) {
    return false;
  }

  if (
    typeof restaurant.is_direct_live === 'undefined' &&
    typeof restaurant.is_ordering_network_live === 'undefined'
  ) {
    return false;
  }

  return !restaurant.is_direct_live && !restaurant.is_ordering_network_live;
}

/**
 * Returns a boolean that describes if a restaurant is not live for Direct but live on Marketplace
 * @param restaurantId {string} restaurant location ID
 * @param resto {object} restaurant object
 * @returns {Promise<boolean>} representing live for direct status
 */
export async function shouldRedirectToMarketplace(restaurantId, resto) {
  try {
    // Avoid redirecting on local. Because local dev hostnames are shared between OD and Marketplace,
    // This would cause an infinite loop.
    const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
    if (deployEnv === DEPLOY_ENVIRONMENTS.local) return false;
    if (!isDirectUrl()) return false;

    // Don't redirect if coming from order-better-network (OBN)
    if (getTrafficSource(restaurantId)) return false;

    let restaurant = resto;

    if (!restaurant) {
      const { data } = await getFromChownowApi({
        endpoint: getRestaurantEndpoint(restaurantId),
        apiVersion: RESTAURANT_API_VERSION,
      });
      restaurant = data;
    }

    if (
      !Object.prototype.hasOwnProperty.call(restaurant, 'is_direct_live') &&
      !Object.prototype.hasOwnProperty.call(
        restaurant,
        'is_ordering_network_live'
      )
    )
      return false;

    return !restaurant.is_direct_live && !restaurant.is_ordering_network_live;
  } catch (e) {
    logMessage(`Failed to redirect to MP: ${e}`);
    return false;
  }
}

export function shouldDirectToWebsiteChannel(restaurant) {
  if (!restaurant || !isDirectUrl()) return false;
  return !restaurant.is_direct_live && restaurant.is_ordering_network_live;
}

export function getIsDirectToMpRedirect() {
  let isDirectToMpRedirect = '';

  if (isSessionStorageAllowed()) {
    isDirectToMpRedirect = JSON.parse(
      sessionStorage.getItem('cn_directToMpRedirect')
    );
  }

  return isDirectToMpRedirect || false;
}

export function setIsDirectToMpRedirect() {
  if (isSessionStorageAllowed()) {
    sessionStorage.setItem('cn_directToMpRedirect', true);
  }
}

export function redirectToMarketplaceMenu({
  restoId,
  hqId,
  orderId,
  shouldReplaceUrl,
  source,
}) {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set('is_mp_redirect', 'true');

  const domain = getHostnameForMarketplace();
  const marketplaceUrl = `https://${domain}/order/${
    orderId ? `history/${orderId}` : `${hqId}/locations/${restoId}`
  }?${searchParams.toString()}`;

  setIsDirectToMpRedirect();

  logAnalyticsEvent({
    eventName: ANALYTICS_EVENT_NAME.redirectToMarketplaceMenu,
    attributes: {
      restaurant_location_id: restoId,
      source,
    },
  });

  if (shouldReplaceUrl) {
    return locationReplace(marketplaceUrl);
  }
  return window.open(marketplaceUrl, '_blank');
}

/**
 * Evaluate whether a URL qualifies for a redirect to Marketplace from OD
 * Qualifying URLs
 * - contain a query with the `cn_instagram` or `cn_facebook` value
 * - Originated on an Order Direct app
 * - Are aiming to hit a menu
 * @param location {object} browser location object with origin, pathname and search keys
 * @returns {boolean} evaluate whether a URL qualifies for an Instagram redirect
 */
export function getURLRequiresRedirect({ pathname, origin, search }) {
  const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
  const INSTAGRAM_QUERY = 'cn_instagram';
  const FACEBOOK_QUERY = 'cn_facebook';
  const LOCATIONS_REGEX = /locations\/\d/gm;
  // Avoid redirecting on local. Because local dev hostnames are shared between OD and Marketplace,
  // This would cause an infinite loop.
  if (deployEnv === DEPLOY_ENVIRONMENTS.local) return false;

  if (
    (search.includes(INSTAGRAM_QUERY) || search.includes(FACEBOOK_QUERY)) &&
    !origin.includes(MARKETPLACE_ORIGINS.prod) &&
    !origin.includes(MARKETPLACE_ORIGINS.qa) &&
    pathname.match(LOCATIONS_REGEX)
  ) {
    return true;
  }
  return false;
}

// If a user lands on ordering with nothing in the URL path, we ultimately want to redirect them
// to the Marketplace homepage
export function getUrlQualifiesForRedirectToMarketplace({ url }) {
  const urlObject = new URL(url);
  const isRouteWithoutPath = urlObject?.pathname === '/';

  return isRouteWithoutPath;
}

export function redirectToMarketplaceHomepage() {
  const redirectTo = getHostnameForMarketplace();
  locationReplace(`https://${redirectTo}`);
}
